// Hand-written: the article body.
//
// Figma 6377:88492 (news detail) and 6377:88526 (blog post). The two disagree on almost
// nothing: both are a **two-column article** on the same 440 / 135 / 785 grid the legal
// pages use - a narrow left rail carrying the title, the excerpt and a date chip, and a
// wide right column carrying the text and the photographs. The only real difference is
// how many photographs the right column holds, and that is content: the news detail puts
// two 385px squares side by side, the blog post one full-width band. So the number of
// images decides the layout, and neither node needs a variant.
//
// Measured from the file: left rail on a 48px stack, title at `h2` (46px), excerpt at
// `body-small` in `grey-900` over a 266px measure, and the date as a **filled black chip**
// at `Radius/s` with 12px `grey-100` text - not the plain timestamp this used to draw.
// Right column on a 20px stack, its heading at `h4` and the text at `body` 16px/1.5 in
// black.
//
// Two things it deliberately does **not** draw, both listed for the designer:
//
// 1. **No category chip.** The `section name` instance in the left rail is the date -
//    CLAUDE.md says so, and the file confirms it. There is no category anywhere on
//    either detail page.
// 2. **No cover image.** Neither node shows one. The cover an editor uploads is the
//    listing card's photograph; on the article page the header is text by composition.
//    Worth confirming, because an editor who uploads a cover will expect to see it.
//
// The section has two modes. Filled in on an ordinary page it renders its own content -
// which is the second block of 6377:88492, a left-rail heading beside text and images.
// Left empty on a page marked as the article layout it renders the article being viewed,
// which is the first block. That is what makes an article detail page composable rather
// than hardcoded in React.
import { SectionShell, Blocks } from '../primitives';
import { Image, SIZES } from '../../components/ui/Image';
import { formatDate } from '../../lib/i18n';
import { useCurrentArticle } from '../../lib/article-context';
import { useSite } from '../../lib/site-context';
import type { ArticleBodySection, StrapiMedia } from '../../types/sections';

/** Figma 6377:88503 - filled black, `Radius/s`, 12px in `grey-100`. */
function Chip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex w-fit items-center rounded-s bg-ink-strong px-4 py-2 text-caption text-grey-100">
      {children}
    </span>
  );
}

/**
 * The photographs under the text.
 *
 * One fills the column (the blog post's 786x442 band), two or more sit side by side as
 * squares (the news detail's pair of 385s). Images placed inside the rich text render
 * inline where the editor put them; this is the row that follows it.
 */
function Photos({ items }: { items?: StrapiMedia[] }) {
  if (!items?.length) return null;
  const single = items.length === 1;

  return (
    <div className={single ? '' : 'grid grid-cols-1 gap-4 sm:grid-cols-2'}>
      {items.map((media, i) => (
        <figure
          key={i}
          className={`overflow-hidden rounded-m bg-surface-sunken ${single ? 'aspect-[16/9]' : 'aspect-square'}`}
        >
          <Image
            media={media}
            sizes={single ? SIZES.half : SIZES.third}
            className="h-full w-full object-cover"
          />
        </figure>
      ))}
    </div>
  );
}

export default function ArticleBody({ data }: { data: ArticleBodySection }) {
  const article = useCurrentArticle();
  const { locale } = useSite();
  const hasOwnContent = Boolean(data.title || data.body?.length || data.images?.length);

  // Neither its own content nor an article to fall back on: an empty article-body on an
  // ordinary page. Draw nothing rather than an empty grid.
  if (!hasOwnContent && !article) return null;

  const showArticle = !hasOwnContent && article;
  const date = article ? formatDate(locale, article.publishedDate) : '';

  return (
    <SectionShell settings={data.settings} name="article-body">
      <div className="grid gap-10 lg:grid-cols-12 lg:gap-0">
        {/* Left rail: 440 of the 1360 grid, on a 48px stack. */}
        <div className="flex flex-col gap-6 lg:col-span-4 lg:gap-12">
          {showArticle ? (
            <>
              {/* The article is what the page is about, so its title is the document's h1. */}
              <h1 className="text-h3 font-normal text-ink-strong lg:text-h2">{article.title}</h1>
              {article.excerpt ? (
                <p className="max-w-[266px] text-body-sm text-ink-muted">{article.excerpt}</p>
              ) : null}
              {date ? (
                <Chip>
                  <time dateTime={article.publishedDate ?? undefined}>{date}</time>
                </Chip>
              ) : null}
            </>
          ) : (
            <>
              {data.title ? (
                <h2 className="text-h4 font-normal text-ink-strong lg:text-h2">{data.title}</h2>
              ) : null}
              {data.eyebrow?.label ? <Chip>{data.eyebrow.label}</Chip> : null}
            </>
          )}
        </div>

        {/* Right column: 785 of the 1360 grid, on a 20px stack. */}
        <div className="flex flex-col gap-5 lg:col-span-7 lg:col-start-6">
          <Blocks value={showArticle ? article.body : data.body} className="max-w-none text-ink-strong" />
          <Photos items={data.images} />
        </div>
      </div>
    </SectionShell>
  );
}
